import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query';
import { QueryFetchFunction, QueryTransformer } from 'common/helpers/common';
import { ApiError, ExtendedApiResult } from 'common/repositories/common.model';

import {
  GuestBirthdayLiteModel,
  GuestDocumentLiteModel,
  GuestFlightScheduleLiteModel,
  GuestHotelScheduleLiteModel,
  getGuestBirthdayInput,
  getGuestDocumentInput,
  getGuestFlightScheduleInput,
  getGuestHotelScheduleInput,
} from './model';

const ENDPOINT = 'cro' as const;

export const croKey = {
  guestBirthdays: 'get-guest-birthdays',
  guestDocuments: 'get-guest-documents',
  flightSchedules: 'get-guest-flight-schedules',
  hotelSchedules: 'get-guest-hotel-schedules',
  guestBirthdaysKey(input?: getGuestBirthdayInput) {
    return [croKey.guestBirthdays, input];
  },
  guestDocumentsKey(input?: getGuestDocumentInput) {
    return [croKey.guestDocuments, input];
  },
  flightSchedulesKey(input?: getGuestFlightScheduleInput) {
    return [croKey.flightSchedules, input];
  },
  hotelSchedulesKey(input?: getGuestHotelScheduleInput) {
    return [croKey.hotelSchedules, input];
  },
} as const;

export function useGetGuestBirthdays(
  input?: getGuestBirthdayInput,
  options?: UseQueryOptions<
    ExtendedApiResult<GuestBirthdayLiteModel[]>,
    ApiError
  >,
): UseQueryResult<ExtendedApiResult<GuestBirthdayLiteModel[]>, ApiError> {
  if (input) {
    input.sort = input.params.sort;
    input.page = input.params.page;
    input.n = input.params.n;
    delete input.params;
  }

  return QueryTransformer(
    useQuery(
      options?.queryKey ?? croKey.guestBirthdaysKey(input),
      () =>
        QueryFetchFunction({
          url: `${ENDPOINT}/guest-birthdays`,
          params: input,
        }),
      options,
    ),
    GuestBirthdayLiteModel,
  );
}

export function useGetGuestDocuments(
  input: getGuestDocumentInput,
  options?: UseQueryOptions<
    ExtendedApiResult<GuestDocumentLiteModel[]>,
    ApiError
  >,
): UseQueryResult<ExtendedApiResult<GuestDocumentLiteModel[]>, ApiError> {
  if (input) {
    // input.filter = { ...input.params?.filter, ...input.filter };
    input.sort = input.params.sort;
    input.page = input.params.page;
    delete input.params;
  }

  return QueryTransformer(
    useQuery(
      options?.queryKey ?? croKey.guestDocumentsKey(input),
      () =>
        QueryFetchFunction({
          url: `${ENDPOINT}/guest-documents`,
          params: input,
        }),
      options,
    ),
    GuestDocumentLiteModel,
  );
}

export function useGetFlightSchedules(
  input?: getGuestFlightScheduleInput,
  options?: UseQueryOptions<
    ExtendedApiResult<GuestFlightScheduleLiteModel[]>,
    ApiError
  >,
): UseQueryResult<ExtendedApiResult<GuestFlightScheduleLiteModel[]>, ApiError> {
  if (input) {
    input.filter = { ...input.params?.filter, ...input.filter };
    input.sort = input.params.sort;
    input.page = input.params.page;
    delete input.params;
  }

  const queryKey = croKey.flightSchedulesKey(input);
  sessionStorage.setItem('flight-schedule-key', JSON.stringify(queryKey));

  return QueryTransformer(
    useQuery(
      options?.queryKey ?? queryKey,
      () =>
        QueryFetchFunction({
          url: `${ENDPOINT}/flight-schedules`,
          params: input,
        }),
      options,
    ),
    GuestFlightScheduleLiteModel,
  );
}

export function useGetHotelSchedules(
  input?: getGuestHotelScheduleInput,
  options?: UseQueryOptions<
    ExtendedApiResult<GuestHotelScheduleLiteModel[]>,
    ApiError
  >,
): UseQueryResult<ExtendedApiResult<GuestHotelScheduleLiteModel[]>, ApiError> {
  if (input) {
    input.filter = { ...input.params?.filter, ...input.filter };
    input.sort = input.params.sort;
    input.page = input.params.page;
    delete input.params;
  }

  const queryKey = croKey.hotelSchedulesKey(input);
  sessionStorage.setItem('hotel-schedule-key', JSON.stringify(queryKey));

  return QueryTransformer(
    useQuery(
      options?.queryKey ?? queryKey,
      () =>
        QueryFetchFunction({
          url: `${ENDPOINT}/hotel-schedules`,
          params: input,
        }),
      options,
    ),
    GuestHotelScheduleLiteModel,
  );
}
